/**
 * Central, type-safe access to build-time environment configuration.
 *
 * Values come from Vite's `import.meta.env` (VITE_-prefixed variables defined
 * in `.env`, `.env.test`, `.env.prod`). Required variables are validated at
 * module load time so a misconfigured build fails fast with a clear error
 * instead of silently falling back to a bogus value.
 */

interface AppEnv {
  apiUrl: string;
  apiBaseUrl: string;
  environment: string;
  auth: {
    authority: string;
    clientId: string;
    redirectUri: string;
    scope: string;
  };
}

function required(name: string, value: string | undefined): string {
  if (value === undefined || value.trim() === '') {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return value;
}

const env: AppEnv = {
  // Legacy backend (custom envelope, /api/v1) — used by not-yet-migrated features.
  apiUrl: required('VITE_BHP_API_URL', import.meta.env.VITE_BHP_API_URL),
  // Backend2.0 origin (API Platform, base /api) — used by the new HttpClient (BHP-1046).
  apiBaseUrl: required('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL),
  environment: import.meta.env.VITE_ENV ?? 'dev',
  // Authentik OIDC (BHP-1040): every value comes from env, no hardcoded fallbacks.
  auth: {
    authority: required(
      'VITE_AUTH_AUTHORITY',
      import.meta.env.VITE_AUTH_AUTHORITY
    ),
    clientId: required(
      'VITE_AUTH_CLIENT_ID',
      import.meta.env.VITE_AUTH_CLIENT_ID
    ),
    redirectUri: required(
      'VITE_AUTH_REDIRECT_URI',
      import.meta.env.VITE_AUTH_REDIRECT_URI
    ),
    scope: required('VITE_AUTH_SCOPE', import.meta.env.VITE_AUTH_SCOPE)
  }
};

export default env;
